import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import EducationalDisclaimer from '@/components/feature/EducationalDisclaimer';

const infoTopics = [
  { category: 'Vehicle Registration', color: 'bg-green-50 border-green-200', icon: 'ri-car-line', iconColor: 'text-green-600', links: [
    { to: '/nevada-vehicle-registration-guide', label: 'Vehicle Registration Guide' },
    { to: '/nevada-registration-renewal', label: 'Registration Renewal' },
    { to: '/nevada-registration-fees', label: 'Registration Fees & Taxes' },
    { to: '/nevada-late-registration-penalties', label: 'Late Registration Penalties' },
    { to: '/nevada-non-operation-affidavit', label: 'Non-Operation Affidavit' },
    { to: '/nevada-new-resident-registration', label: 'New Resident Registration' },
    { to: '/nevada-out-of-state-vehicle', label: 'Out-of-State Vehicle Registration' },
  ]},
  { category: 'Title & Ownership', color: 'bg-purple-50 border-purple-200', icon: 'ri-file-text-line', iconColor: 'text-purple-600', links: [
    { to: '/nevada-title-transfer-guide', label: 'Title Transfer Guide' },
    { to: '/nevada-duplicate-title', label: 'Duplicate Title Process' },
    { to: '/nevada-salvage-title', label: 'Salvage Title Information' },
    { to: '/nevada-lien-release', label: 'Lien Release Process' },
    { to: '/nevada-title-after-death', label: 'Title Transfer After Death' },
    { to: '/nevada-private-party-sale', label: 'Private Party Vehicle Sale' },
    { to: '/nevada-bill-of-sale', label: 'Bill of Sale Requirements' },
  ]},
  { category: "Driver's License", color: 'bg-blue-50 border-blue-200', icon: 'ri-id-card-line', iconColor: 'text-blue-600', links: [
    { to: '/nevada-drivers-license-guide', label: "Driver's License Guide" },
    { to: '/nevada-real-id', label: 'REAL ID Requirements' },
    { to: '/nevada-teen-driver', label: 'Teen Driver Licensing' },
    { to: '/nevada-license-renewal', label: 'License Renewal Process' },
    { to: '/nevada-cdl-guide', label: 'Commercial Driver\'s License (CDL)' },
    { to: '/nevada-motorcycle-license', label: 'Motorcycle License & Endorsement' },
  ]},
  { category: 'License Plates', color: 'bg-yellow-50 border-yellow-200', icon: 'ri-price-tag-3-line', iconColor: 'text-yellow-600', links: [
    { to: '/nevada-license-plates-guide', label: 'License Plates Overview' },
    { to: '/nevada-specialty-plates', label: 'Specialty License Plates' },
    { to: '/nevada-personalized-plates', label: 'Personalized Plates' },
    { to: '/nevada-veteran-plates', label: 'Veteran License Plates' },
    { to: '/nevada-disabled-parking', label: 'Disabled Parking Placards' },
  ]},
  { category: 'Emissions & Inspections', color: 'bg-teal-50 border-teal-200', icon: 'ri-leaf-line', iconColor: 'text-teal-600', links: [
    { to: '/nevada-smog-emissions-guide', label: 'Smog & Emissions Testing' },
    { to: '/nevada-vin-inspection', label: 'VIN Inspection Requirements' },
    { to: '/nevada-emissions-exemptions', label: 'Emissions Exemptions' },
    { to: '/nevada-emissions-waiver', label: 'Emissions Waiver Process' },
  ]},
  { category: 'Specialty Vehicles', color: 'bg-orange-50 border-orange-200', icon: 'ri-truck-line', iconColor: 'text-orange-600', links: [
    { to: '/nevada-ohv-registration', label: 'Off-Highway Vehicle (OHV) Registration' },
    { to: '/nevada-trailer-registration', label: 'Trailer Registration' },
    { to: '/nevada-commercial-vehicle', label: 'Commercial Vehicle Registration' },
    { to: '/nevada-temporary-permits-guide', label: 'Temporary Operating Permits' },
  ]},
  { category: 'DMV Processes', color: 'bg-red-50 border-red-200', icon: 'ri-government-line', iconColor: 'text-red-600', links: [
    { to: '/nevada-dmv-appointment', label: 'DMV Appointment Guide' },
    { to: '/nevada-power-of-attorney-vehicle', label: 'Power of Attorney for Vehicles' },
    { to: '/nevada-dmv-fees-guide', label: 'Complete DMV Fee Guide' },
    { to: '/nevada-dmv-forms', label: 'Nevada DMV Forms Directory' },
  ]},
];

const NevadaDMVInfoPage: React.FC = () => (
  <div className="min-h-screen bg-white">
    <Header />

    <section className="pt-28 pb-14 bg-obsidian-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-gold-400 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="flex items-center space-x-2 text-sm text-obsidian-400 mb-4">
          <Link to="/" className="hover:text-gold-400 transition-colors">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gold-400">Nevada DMV Information Hub</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-4">
          Nevada DMV <span className="text-gold-400">Information Hub</span>
        </h1>
        <p className="text-obsidian-300 text-lg max-w-2xl">
          Comprehensive educational guides on Nevada DMV processes, requirements, fees, and forms. All information is for educational purposes only.
        </p>
      </div>
    </section>

    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <EducationalDisclaimer />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {infoTopics.map(topic => (
          <div key={topic.category} className={`border rounded-xl p-6 ${topic.color}`}>
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg">
                <i className={`${topic.icon} ${topic.iconColor} text-lg`}></i>
              </div>
              <h2 className="font-display font-bold text-obsidian-900 text-base">{topic.category}</h2>
            </div>
            <ul className="space-y-2">
              {topic.links.map(link => (
                <li key={link.to}>
                  <Link to={link.to} className="flex items-center space-x-2 text-sm text-obsidian-700 hover:text-gold-600 transition-colors">
                    <i className="ri-arrow-right-s-line text-obsidian-400 text-xs"></i>
                    <span>{link.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mt-12 bg-obsidian-900 rounded-xl p-8 text-center">
        <h2 className="font-display text-2xl font-bold text-white mb-3">Skip the DMV Hassle</h2>
        <p className="text-obsidian-300 text-sm mb-6 max-w-xl mx-auto">Speedy DMV Services handles all Nevada DMV transactions for you — vehicle registration, title transfers, license plates, and more. Fast, professional, and stress-free.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <Link to="/appointment" className="bg-gold-gradient text-obsidian-900 px-6 py-3 rounded-lg font-bold text-sm hover:brightness-110 transition-all whitespace-nowrap">Book Appointment</Link>
          <a href="tel:7029797202" className="border border-gold-500/50 text-gold-400 px-6 py-3 rounded-lg font-semibold text-sm hover:bg-gold-500/10 transition-all whitespace-nowrap">Call 702.979.7202</a>
        </div>
      </div>
    </div>

    <Footer />
  </div>
);

export default NevadaDMVInfoPage;
